import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Stack, Typography } from '@mui/material';
import NcplLogo from '../brand/NcplLogo';
import SedexLogo from '../common/SedexLogo';
import { company, formatRegisteredOffice } from '../../config/company';
import { footerCapabilities, footerCompany, footerSourcing } from '../../config/navigation';

const linkSx = {
  display: 'block',
  color: 'rgba(247,244,237,0.78)',
  textDecoration: 'none',
  py: 0.6,
  '&:hover': { color: 'secondary.light' },
};

function LegalContact() {
  const email = company.contact.businessEmail;
  const phone = company.contact.phone;
  const office = formatRegisteredOffice();
  const cin = company.cin;

  return (
    <Box>
      <Typography className="eyebrow" component="h2" sx={{ color: 'secondary.light' }}>
        Contact
      </Typography>
      <Typography sx={{ color: 'rgba(247,244,237,0.88)', fontWeight: 600, mb: 1 }}>
        {company.legalName}
      </Typography>
      <Typography component="address" sx={{ color: 'rgba(247,244,237,0.72)', fontStyle: 'normal', m: 0 }}>
        {office ? (
          <Box component="span" sx={{ display: 'block', mb: 1.25 }}>
            <Box component="span" sx={{ display: 'block', mb: 0.4, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(247,244,237,0.45)' }}>
              Registered Office
            </Box>
            <Box component="span" sx={{ display: 'block', whiteSpace: 'pre-line' }}>{office}</Box>
          </Box>
        ) : (
          <Box component="span" sx={{ display: 'block' }}>{company.location.display}</Box>
        )}
      </Typography>
      {cin ? (
        <Typography sx={{ color: 'rgba(247,244,237,0.55)', fontSize: 13, mt: 1.25, mb: 0 }}>
          CIN: {cin}
        </Typography>
      ) : null}
      {email ? (
        <Box component="a" href={`mailto:${email}`} sx={{ ...linkSx, overflowWrap: 'anywhere' }}>
          {email}
        </Box>
      ) : null}
      {phone?.display && phone?.href ? (
        <Box
          component="a"
          href={`tel:${phone.href}`}
          sx={{ ...linkSx, color: 'rgba(247,244,237,0.5)', fontSize: 13 }}
        >
          {phone.display}
        </Box>
      ) : null}
    </Box>
  );
}

function Column({ title, links }) {
  return (
    <Box>
      <Typography className="eyebrow" component="h2" sx={{ color: 'secondary.light' }}>
        {title}
      </Typography>
      {links.map((item) => (
        <Box key={item.to} component={RouterLink} to={item.to} sx={linkSx}>
          {item.label}
        </Box>
      ))}
    </Box>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <Box component="footer" sx={{ backgroundColor: 'primary.dark', color: 'common.white', pt: { xs: 7, md: 9 }, pb: 3 }}>
      <Container>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1.4fr 0.8fr 0.8fr 0.9fr 1.1fr' },
            gap: { xs: 4, md: 4 },
          }}
        >
          <Box>
            <Box component={RouterLink} to="/" aria-label="NCPL home" sx={{ display: 'inline-flex', textDecoration: 'none', mb: 2 }}>
              <NcplLogo variant="dark" />
            </Box>
            <Typography component="p" sx={{ color: 'rgba(247,244,237,0.72)', maxWidth: 320, m: 0 }}>
              Specialist woven shirt manufacturer in Kolkata, India, producing men’s and kids’ shirts for large retail and private-label programmes.
            </Typography>
          </Box>
          <Column title="Company" links={footerCompany} />
          <Column title="Capabilities" links={footerCapabilities} />
          <Column title="Global Sourcing" links={footerSourcing} />
          <LegalContact />
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={1}
          sx={{ mt: 7, pt: 2.5, borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(247,244,237,0.55)', fontSize: 13 }}
        >
          <span>© {year} {company.legalName}</span>
          <Box component={RouterLink} to="/responsibility" sx={{ display: 'inline-flex', lineHeight: 0 }}>
            <SedexLogo tone="dark" height={20} />
          </Box>
          <span>Kolkata, West Bengal, India</span>
        </Stack>
      </Container>
    </Box>
  );
}
